import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { paypalPaymentService } from '../membership-workflow/paypalPaymentService';
import axios from '../../config/axios';

/*
Cash ok
Stripe non deve fare niente perche' authContex ha il token di authenticazione e quindi fa un /me
Paypal deve fare la capture e poi mettersi in polling finche' il server non riceve il webhook PAYMENT.CAPTURE.COMPLETED e aggiorna l'enrollment
*/

const CourseEnrollmentCallback = () => {
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'cancelled', 'error'
  const [paymentMethod, setPaymentMethod] = useState(''); // 'stripe', 'paypal', 'cash'
  const [isFreeEnrollment, setIsFreeEnrollment] = useState(false);

  // capture id sarebbe paypalPaymentId
  const waitForPaymentUpdate = async (captureId) => {
    const maxAttempts = 10;
    const fixedDelay = 5000; // 3 secondi fisso
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await axios.get('/api/user/me');
        const user = response.data;
        
        // console.log('🔴 User payments:', user.payments);
        // console.log('🔴 Capture ID:', captureId);
        const payment = user.payments?.find(p => 
          p.paypalPaymentId === captureId
        );
        
        if (payment) {
          console.log('✅ Course enrollment updated successfully');
          updateUser(user);
          return;
        }
        
        // Se non è ancora aggiornato, aspetta 3 secondi prima del prossimo tentativo
        if (attempt < maxAttempts) {
          console.log(`⏳ Course enrollment not yet updated, retrying in ${fixedDelay}ms (attempt ${attempt}/${maxAttempts})`);
          await new Promise(resolve => setTimeout(resolve, fixedDelay));
        }
      } catch (error) {
        console.error(`❌ Error checking user data (attempt ${attempt}):`, error);
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, fixedDelay));
        }
      }
    }
    
    // Se arriviamo qui, l'enrollment non è stato aggiornato
    console.warn('⚠️ Course enrollment update timeout - webhook may not have arrived');
    // Lancia un errore per indicare che il webhook non è arrivato
    throw new Error('Timeout. Paypal sta sperimentando dei ritardi, attendi qualche minuto e prova a loggarti di nuovo. Se il problema persiste, contatta la segreteria.');    
  };

  const handleSuccessfulPayment = async (method, urlParams) => {
    try {
      if (method === 'cash') {
        setStatus('success');
        setLoading(false);
      }
      if (method === 'stripe') {
        setStatus('success');
        setLoading(false);
      }
      if (method === 'paypal') {
        // Handle PayPal payment capture
        try {
          const orderId = urlParams.get('token'); // PayPal returns token as orderId
          if (orderId) {
            const captureResult = await paypalPaymentService.capturePayPalCourseOrder(orderId);
            if (captureResult.success) {
              // Estrai il capture.id dalla risposta di PayPal
              const captureId = captureResult.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.id;
              if (captureId) {
                console.log('🔴 Course Capture ID:', captureId);
                // Polling per aspettare che il webhook arrivi e aggiorni l'enrollment
                await waitForPaymentUpdate(captureId);
                // Solo dopo che waitForEnrollmentUpdate è completato, aggiorna lo stato
                setStatus('success');
                setLoading(false);
              } else {
                console.error('Capture ID non trovato nella risposta PayPal');
                setError('Capture ID non trovato nella risposta PayPal');
                setStatus('error');
                setLoading(false);
              }
            } else {
              console.error('PayPal capture failed:', captureResult.error);
              setError('PayPal capture failed:' + captureResult.error);
              setStatus('error');
              setLoading(false);
            }
          } else {
            console.error('PayPal orderId not found in URL params');
            setError('PayPal orderId not found in URL params');
            setStatus('error');
            setLoading(false);
          }
        } catch (error) {
          console.error('Errore nella gestione del pagamento PayPal:' + error);
          setError('Errore nella gestione del pagamento PayPal:' + error);
          setStatus('error');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error handling successful payment:', error);
      setError('Errore nella gestione del pagamento');
      setStatus('error');
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentStatus = urlParams.get('payment_status');
    const method = urlParams.get('payment_method');
    const amount = urlParams.get('amount');

    setPaymentMethod(method);
    setStatus(paymentStatus);
    
    // Check if this is a free enrollment (amount = 0)
    if (amount === '0') {
      setIsFreeEnrollment(true);
    }
    
    // console.log('paymentStatus', paymentStatus);
    // console.log('method', method);
    // console.log('amount', amount);
    // Se il pagamento è riuscito, gestiscilo
    if (paymentStatus === 'success') {
      handleSuccessfulPayment(method, urlParams);
    } else {
      // Solo se NON è success, allora possiamo settare loading a false
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderProcessing = () => (
    <Container maxWidth='sm'>
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant='h5' gutterBottom>
          Verifica in corso...
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          Stiamo verificando il tuo pagamento
        </Typography>
      </Box>
    </Container>
  );

  const renderSuccess = () => (
    <Container maxWidth='sm'>
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
        <Typography variant='h4' gutterBottom color='success.main'>
          {isFreeEnrollment 
            ? 'Iscrizione Completata!' 
            : paymentMethod === 'cash' 
              ? 'Iscrizione Effettuata' 
              : 'Pagamento Completato!'
          }
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
          {isFreeEnrollment 
            ? 'La tua lezione di prova è stata prenotata con successo! Non è richiesto alcun pagamento. Riceverai una email di conferma a breve.\n\nBenvenuto!'
            : paymentMethod === 'cash' 
              ? 'La tua iscrizione al corso è stata effettuata, ora potrai partecipare alle lezioni.\n\nRicorda che hai scelto il metodo di pagamento in contanti, dovrai quindi saldare la tua quota direttamente presso la segreteria, grazie!'
              : 'La tua iscrizione al corso è stata completata con successo. Riceverai una email di conferma a breve.'
          }
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
          <Button variant='contained' onClick={() => navigate('/my-courses')}>
            Vai alle Mie Iscrizioni
          </Button>
          <Button variant='contained' onClick={() => navigate('/courses')}>
            Torna ai Corsi
          </Button>
        </Box>
      </Box>
    </Container>
  );

  const renderCancelled = () => (
    <Container maxWidth='sm'>
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 80, color: 'warning.main', mb: 3 }} />
        <Typography variant='h4' gutterBottom color='warning.main'>
          Pagamento Annullato
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
          Il pagamento è stato annullato. <br />
          Non è stato addebitato alcun importo.
          <br />
          Se desideri completare l'iscrizione al corso, puoi riprovare quando vuoi.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
          <Button variant='contained' onClick={() => navigate('/courses')}>
            Torna ai Corsi
          </Button>
          <Button variant='contained' onClick={() => navigate('/')}>
            Torna alla Home
          </Button>
        </Box>
      </Box>
    </Container>
  );

  const renderError = () => (
    <Container maxWidth='sm'>
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 3 }} />
        <Typography variant='h4' gutterBottom color='error.main'>
          Errore nel Pagamento
        </Typography>
        <Alert severity='error' sx={{ mb: 4, textAlign: 'left' }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
          <Button variant='contained' onClick={() => navigate('/courses')}>
            Torna ai Corsi
          </Button>
          <Button variant='contained' onClick={() => navigate('/')}>
            Torna alla Home
          </Button>
        </Box>
      </Box>
    </Container>
  );

  if (loading) {
    return renderProcessing();
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1 }}>{status === 'success' ? renderSuccess() : status === 'cancelled' ? renderCancelled() : renderError()}</Box>
    </Box>
  );
};

export default CourseEnrollmentCallback; 